import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Provider } from "react-redux";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline, Snackbar, Alert } from "@mui/material";
import { store } from "./store";
import { theme } from "./theme";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { fetchAllData } from "./store/slices/dataSlice";
import { hideSnackbar } from "./store/slices/uiSlice";
import Shell from "./components/layout/Shell";
import Login from "./pages/Login/Login";
import Dashboard from "./pages/Dashboard/Dashboard";
import Chat from "./pages/Chat/Chat";
import Employees from "./pages/Employees/Employees";
import Attendance from "./pages/Attendance/Attendance";
import Leave from "./pages/Leave/Leave";
import Payroll from "./pages/Payroll/Payroll";
import Recruitment from "./pages/Recruitment/Recruitment";
import Onboarding from "./pages/Onboarding/Onboarding";
import Performance from "./pages/Performance/Performance";
import Training from "./pages/Training/Training";
import Departments from "./pages/Departments/Departments";
import { Reports, Settings, MyProfile } from "./pages/Reports/Reports";

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const snackbar = useAppSelector((s) => s.ui.snackbar);
  const users = useAppSelector((s) => s.data.users);
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated && users.length === 0) {
      dispatch(fetchAllData());
    }
  }, [dispatch, users.length, isAuthenticated]);
  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/app"
            element={
              <PrivateRoute>
                <Shell />
              </PrivateRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="chat" element={<Chat />} />
            <Route path="employees" element={<Employees />} />
            <Route path="attendance" element={<Attendance />} />
            <Route path="leave" element={<Leave />} />
            <Route path="payroll" element={<Payroll />} />
            <Route path="recruitment" element={<Recruitment />} />
            <Route path="onboarding" element={<Onboarding />} />
            <Route path="performance" element={<Performance />} />
            <Route path="training" element={<Training />} />
            <Route path="departments" element={<Departments />} />
            <Route path="reports" element={<Reports />} />
            <Route path="settings" element={<Settings />} />
            <Route path="myprofile" element={<MyProfile />} />
          </Route>
          <Route path="/" element={<Navigate to="/app/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => dispatch(hideSnackbar())}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => dispatch(hideSnackbar())}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

import { HelmetProvider } from 'react-helmet-async';
import { SocketProvider } from './context/SocketContext';
import IncomingCall from './components/chat/IncomingCall';

const AppContentWrapped: React.FC = () => {
  return (
    <>
      <AppContent />
      <IncomingCall />
    </>
  );
};

const App: React.FC = () => (
  <HelmetProvider>
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SocketProvider>
          <AppContentWrapped />
        </SocketProvider>
      </ThemeProvider>
    </Provider>
  </HelmetProvider>
);

export default App;
